import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { InputSwitch } from 'primereact/inputswitch';
import { ProgressBar } from 'primereact/progressbar';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
    calculateProgress,
    cancelOrgSubscription,
    getOrgSubscriptionDetails,
    getOrgSubscriptionInfo,
    getOrgSubscriptionPortal,
    type _CancelSubscriptionPayload,
    type _OrgSubscriptionDetailsData,
} from '../../../../helpers/orgSubscriptionHelper';
import { ORG_SUBSCRIPTION_DETAILS, ORG_SUBSCRIPTION_INFO } from '../../../../constants/queryKeys';
import logger from '../../../../utils/logger';
import MlxBlockUI from '../../../../components/common/MlxBlockUI/MlxBlockUI';
import MlxToast from '../../../../components/common/MlxToast/MlxToast';
import { _ToastMessageProps } from '../../../../constants/staticTypes';
import { defaultToastData } from '../../../../helpers/commonHelper';
import './OrgSubscription.scss';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../../../routes/allRoutes';
import { useTranslation } from '../../../../hooks/useTranslation';
import {
    INVOICE_HISTORY,
    TRIAL_DAYS_LEFT,
    UPGRADE,
    SUBSCRIBE,
    PLAN_STARTED_ON,
    NEXT_BILLING,
    DAY_X_OF_Y,
    CURRENT_PLAN_LABEL,
    PAYMENT_METHOD,
    STRIPE_COMPONENT_PLACEHOLDER,
    ACCOUNT_OWNER,
    ACTIVE,
    AUTO_RENEWAL,
    AUTO_RENEWAL_DESCRIPTION,
    FAILED_TO_FETCH_SUBSCRIPTION,
    UPCOMING_PLAN,
    UPCOMING_PLAN_STARTS_ON,
    CANCEL_SUBSCRIPTION,
    CANCEL_SUBSCRIPTION_DESC,
    FAILED_TO_CANCEL_SUBSCRIPTION,
    CARD_ADDED,
    CARD_NOT_ADDED,
    CHANGE_PAYMENT_METHOD,
    ADD_NEW_CARD,
    FAILED_TO_LOAD_PAYMENT_PORTAL,
    NO_ACTIVE_SUBSCRIPTION,
    NO_PAYMENT_METHOD_WARNING,
    SUBSCRIPTION_CANCELLED_ALERT,
} from '../../../../constants/strings';
import InvoiceHistory from './InvoiceHistory';
import CancelSubscriptionModal from './CancelSubscriptionModal';

const languageTexts = {
    INVOICE_HISTORY,
    TRIAL_DAYS_LEFT,
    UPGRADE,
    SUBSCRIBE,
    PLAN_STARTED_ON,
    NEXT_BILLING,
    DAY_X_OF_Y,
    CURRENT_PLAN_LABEL,
    PAYMENT_METHOD,
    STRIPE_COMPONENT_PLACEHOLDER,
    ACCOUNT_OWNER,
    ACTIVE,
    AUTO_RENEWAL,
    AUTO_RENEWAL_DESCRIPTION,
    FAILED_TO_FETCH_SUBSCRIPTION,
    UPCOMING_PLAN,
    UPCOMING_PLAN_STARTS_ON,
    CANCEL_SUBSCRIPTION,
    CANCEL_SUBSCRIPTION_DESC,
    FAILED_TO_CANCEL_SUBSCRIPTION,
    CARD_ADDED,
    CARD_NOT_ADDED,
    CHANGE_PAYMENT_METHOD,
    ADD_NEW_CARD,
    FAILED_TO_LOAD_PAYMENT_PORTAL,
    NO_ACTIVE_SUBSCRIPTION,
    NO_PAYMENT_METHOD_WARNING,
    SUBSCRIPTION_CANCELLED_ALERT,
};

const Overview = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const translatedText = useTranslation(languageTexts, 'none');
    const [showInvoiceHistory, setShowInvoiceHistory] = useState(false);
    const [autoRenewal, setAutoRenewal] = useState(true);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [pendingCancelPayload, setPendingCancelPayload] = useState<_CancelSubscriptionPayload | null>(null);
    const [toastMessage, setToastMessage] = useState<_ToastMessageProps>(defaultToastData);

    const { mutate: cancelSubscriptionMutate, isLoading: isCancelling } = useMutation(
        (cancelData: _CancelSubscriptionPayload) => cancelOrgSubscription(queryClient, cancelData),
        {
            onSuccess: () => {
                window.location.reload();
            },
            onError: (error: any) => {
                logger.error('Failed to cancel subscription', error);
                setToastMessage({
                    message:
                        error?.response?.data?.message || translatedText.FAILED_TO_CANCEL_SUBSCRIPTION || FAILED_TO_CANCEL_SUBSCRIPTION,
                    variant: 'error',
                    show: true,
                });
            },
        }
    );

    const handleCancelSubscription = () => {
        const orgProductPlanData = subscriptionResponse?.data?.data?.organizationProductPlan;
        const planData = orgProductPlanData?.plan;
        const cancelPayload = {
            id: orgProductPlanData?.subscriptionId || orgProductPlanData?.id || '',
            name: planData?.name || '',
            description: planData?.description || '',
            isAddon: false,
            sourceType: 'ORGANIZATION',
            sourceId: orgProductPlanData?.id || '',
            isActive: true,
            metadata: {},
        };
        setPendingCancelPayload(cancelPayload);
        setShowCancelDialog(true);
    };

    // Fetch billing portal URL on-demand (called on button click)
    const { mutate: fetchPortalUrl, isLoading: isPortalLoading } = useMutation(() => getOrgSubscriptionPortal(queryClient), {
        onSuccess: (response: any) => {
            const portalUrl = response?.data?.data?.portal_url;
            if (portalUrl) {
                window.open(portalUrl, '_blank', 'noopener,noreferrer');
            } else {
                setToastMessage({
                    message: translatedText.FAILED_TO_LOAD_PAYMENT_PORTAL || FAILED_TO_LOAD_PAYMENT_PORTAL,
                    variant: 'error',
                    show: true,
                });
            }
        },
        onError: (error: any) => {
            logger.error('Failed to fetch payment portal', error);
            setToastMessage({
                message: error?.response?.data?.message || translatedText.FAILED_TO_LOAD_PAYMENT_PORTAL || FAILED_TO_LOAD_PAYMENT_PORTAL,
                variant: 'error',
                show: true,
            });
        },
    });

    // Fetch subscription info (payment details)
    const { data: subscriptionInfoResponse, isLoading: isInfoLoading } = useQuery({
        queryKey: ORG_SUBSCRIPTION_INFO,
        queryFn: () => getOrgSubscriptionInfo(queryClient),
        retry: false,
        refetchOnWindowFocus: false,
    });

    // Fetch organization subscription details from API
    const {
        data: subscriptionResponse,
        isLoading,
        isFetching,
    } = useQuery({
        queryKey: ORG_SUBSCRIPTION_DETAILS,
        queryFn: () => getOrgSubscriptionDetails(queryClient),
        onError: (error) => {
            logger.error('Failed to fetch subscription details', error);
            setToastMessage({
                message: translatedText.FAILED_TO_FETCH_SUBSCRIPTION || FAILED_TO_FETCH_SUBSCRIPTION,
                variant: 'error',
                show: true,
            });
        },
        retry: false,
        refetchOnWindowFocus: false,
    });

    const goToAllPlans = () => {
        navigate(`${routes.orgSubscriptions}?tab=all`);
    };

    if (showInvoiceHistory) {
        return <InvoiceHistory onBack={() => setShowInvoiceHistory(false)} />;
    }

    // Extract data from API response
    const apiData = subscriptionResponse?.data?.data as _OrgSubscriptionDetailsData | undefined;
    const plan = apiData?.organizationProductPlan?.plan;
    const orgProductPlan = apiData?.organizationProductPlan;
    const currentPrice = apiData?.organizationProductPlan?.price;

    // Determine billing cycle from interval
    const billingCycle =
        currentPrice?.recurring?.interval === 'year'
            ? 'Yearly'
            : currentPrice?.recurring?.interval === 'month' && currentPrice?.recurring?.interval_count === 3
              ? 'Quarterly'
              : 'Monthly';

    // Extract marketing features
    const rawFeatures = plan?.additionalData?.marketing_features || [];
    const features = rawFeatures.map((f: string | { name: string }) => (typeof f === 'string' ? f : f?.name)).filter(Boolean);

    // Format dates
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    // Upcoming plan detection: when nextPeriodItems has a different productId than currentItems
    const nextPeriodItems = apiData?.nextPeriodItems || [];
    const currentProductId = apiData?.currentItems?.[0]?.productId;
    const nextProductId = nextPeriodItems[0]?.productId;
    const hasUpcomingPlanChange = nextPeriodItems.length > 0 && !!currentProductId && !!nextProductId && currentProductId !== nextProductId;

    // Extract a clean plan name from description like "1 × Platinum Tier 1 (at $49.99 / month)"
    const extractPlanName = (description: string): string => {
        const match = description?.match(/×\s+(.+?)\s+\(/);
        return match ? match[1] : description || 'N/A';
    };

    const upcomingPlanDescription = nextPeriodItems[0]?.description || '';
    const upcomingPlanName = extractPlanName(upcomingPlanDescription);
    const upcomingPlanStartDate = nextPeriodItems[0]?.periodStart ? formatDate(nextPeriodItems[0].periodStart) : 'N/A';

    // --- Trial/Subscription Period Calculation (using currentItems) ---
    const now = new Date();
    let trialDaysLeft = 0;
    let totalTrialDays = 0;
    let daysUsed = 0;
    let progressPercentage = 0;
    let periodStart = null;
    let periodEnd = null;
    if (apiData && Array.isArray(apiData.currentItems) && apiData.currentItems.length > 0) {
        periodStart = apiData.currentItems[0].periodStart;
        periodEnd = apiData.currentItems[0].periodEnd;
    }
    if (periodStart && periodEnd) {
        const start = new Date(periodStart);
        const end = new Date(periodEnd);
        totalTrialDays = Math.max(Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)), 1);
        trialDaysLeft = Math.max(Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)), 0);
        daysUsed = Math.min(Math.max(Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)), 0), totalTrialDays);
        progressPercentage = totalTrialDays > 0 ? Math.min(((daysUsed + 1) / totalTrialDays) * 100, 100) : 100;
    }

    // Prepare subscription data
    const subscriptionData = {
        trialDaysLeft,
        planName: plan?.name || 'N/A',
        price: apiData?.nextAmountPayableInDollars ?? (currentPrice?.amount ? parseFloat(currentPrice.amount) : 0),
        billingCycle,
        planStartDate: periodStart ? formatDate(periodStart) : 'N/A',
        nextBillingDate: apiData?.nextBillingDate ? formatDate(apiData.nextBillingDate) : 'N/A',
        currentDay: daysUsed + 1,
        totalDays: totalTrialDays,
        features,
        onTrial: apiData?.onTrial || false,
        // TODO: Get account owner data from API when available
        // accountOwner: {
        //     name: 'Daniel James',
        //     role: 'Organization Admin',
        //     avatar: null,
        // },
    };

    return (
        <MlxBlockUI isLoading={isLoading || isFetching || isCancelling || isInfoLoading}>
            <div className="overview-container" data-testid="overview-content">
                {/* Trial Period Alert - Only show when on trial */}
                {subscriptionData.onTrial && subscriptionData.trialDaysLeft > 0 && (
                    <div className="trial-alert">
                        <i className="ri-error-warning-line alert-icon" />
                        <span>
                            {(translatedText.TRIAL_DAYS_LEFT || TRIAL_DAYS_LEFT).replace('{0}', subscriptionData.trialDaysLeft.toString())}
                        </span>
                    </div>
                )}

                {/* Current Plan Section */}
                <div className="plan-section">
                    <div className="plan-header">
                        <div className="plan-info">
                            {subscriptionInfoResponse?.data?.data?.hasSubscription ? (
                                <>
                                    <h2 className="plan-name">{subscriptionData.planName}</h2>
                                    <div className="plan-price">
                                        <span className="price">$ {subscriptionData.price.toFixed(2)}</span>
                                        <span className="billing-cycle">/ {subscriptionData.billingCycle}</span>
                                    </div>
                                </>
                            ) : (
                                <h2 className="plan-name no-subscription">
                                    {translatedText.NO_ACTIVE_SUBSCRIPTION || NO_ACTIVE_SUBSCRIPTION}
                                </h2>
                            )}
                        </div>

                        {!apiData?.markedAsCanceled && (
                            <Button
                                label={
                                    !subscriptionInfoResponse?.data?.data?.hasSubscription
                                        ? translatedText.SUBSCRIBE || SUBSCRIBE
                                        : translatedText.UPGRADE || UPGRADE
                                }
                                icon="ri-arrow-right-line"
                                iconPos="right"
                                className="upgrade-btn"
                                onClick={goToAllPlans}
                            />
                        )}
                    </div>

                    {/* Plan Dates */}
                    <div className="plan-dates">
                        <div className="date-item">
                            <i className="ri-calendar-line" />
                            <span className="date-label">{translatedText.PLAN_STARTED_ON || PLAN_STARTED_ON}</span>
                            <span className="date-badge">{subscriptionData.planStartDate}</span>
                        </div>

                        {!apiData?.markedAsCanceled && (
                            <div className="next-billing">
                                <span className="billing-label">{translatedText.NEXT_BILLING || NEXT_BILLING}</span>
                                <span className="billing-date">{subscriptionData.nextBillingDate}</span>
                            </div>
                        )}
                    </div>

                    {/* Progress Bar - Only show during trial */}
                    {subscriptionData.onTrial && (
                        <div className="progress-section">
                            <div className="progress-label">
                                {(translatedText.DAY_X_OF_Y || DAY_X_OF_Y)
                                    .replace('{0}', subscriptionData.currentDay.toString())
                                    .replace('{1}', subscriptionData.totalDays.toString())}
                            </div>
                            <ProgressBar value={progressPercentage} showValue={false} className="subscription-progress" />
                        </div>
                    )}

                    {/* Invoice History Button */}
                    <div className="invoice-history-section">
                        <Button
                            label={translatedText.INVOICE_HISTORY || INVOICE_HISTORY}
                            icon="ri-file-list-3-line"
                            className="invoice-history-btn"
                            outlined
                            onClick={() => setShowInvoiceHistory(true)}
                        />
                    </div>
                </div>

                {/* Cancelled Subscription Banner */}
                {apiData?.markedAsCanceled && (
                    <div className="cancelled-alert">
                        <i className="ri-close-circle-line alert-icon" />
                        <span>{translatedText.SUBSCRIPTION_CANCELLED_ALERT || SUBSCRIPTION_CANCELLED_ALERT}</span>
                    </div>
                )}

                {/* Payment Method Section */}
                {(() => {
                    const paymentDetails = subscriptionInfoResponse?.data?.data?.paymentDetails;
                    const hasPaymentMethod = paymentDetails?.hasPaymentMethod;
                    return (
                        <div className="payment-method-section">
                            <div className="payment-method-header">
                                <i className="ri-bank-card-line" />
                                <span>{translatedText.PAYMENT_METHOD || PAYMENT_METHOD}</span>
                            </div>
                            <div className="payment-method-body">
                                <div className="payment-method-status">
                                    {hasPaymentMethod ? (
                                        <>
                                            <i className="ri-checkbox-circle-line card-added-icon" />
                                            <span>{translatedText.CARD_ADDED || CARD_ADDED}</span>
                                        </>
                                    ) : (
                                        <>
                                            <i className="ri-information-line card-missing-icon" />
                                            <span>{translatedText.CARD_NOT_ADDED || CARD_NOT_ADDED}</span>
                                        </>
                                    )}
                                </div>
                                <Button
                                    label={
                                        hasPaymentMethod
                                            ? translatedText.CHANGE_PAYMENT_METHOD || CHANGE_PAYMENT_METHOD
                                            : translatedText.ADD_NEW_CARD || ADD_NEW_CARD
                                    }
                                    className="payment-method-btn"
                                    outlined
                                    loading={isPortalLoading}
                                    disabled={isPortalLoading}
                                    onClick={() => fetchPortalUrl()}
                                />
                            </div>
                            {!hasPaymentMethod && (
                                <div className="payment-method-warning">
                                    <i className="ri-error-warning-fill payment-warning-icon" />
                                    <span>{translatedText.NO_PAYMENT_METHOD_WARNING || NO_PAYMENT_METHOD_WARNING}</span>
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* Cancel Subscription Section */}
                {(() => {
                    const infoData = subscriptionInfoResponse?.data?.data;
                    const showCancel =
                        infoData?.hasSubscription === true && !!apiData?.subscriptionId && apiData?.markedAsCanceled !== true;
                    return showCancel ? (
                        <div className="cancel-subscription-section">
                            <div className="cancel-section-text">
                                <div className="cancel-section-title">
                                    <i className="ri-forbid-line" />
                                    <span>{translatedText.CANCEL_SUBSCRIPTION || CANCEL_SUBSCRIPTION}</span>
                                </div>
                                <p className="cancel-section-desc">{translatedText.CANCEL_SUBSCRIPTION_DESC || CANCEL_SUBSCRIPTION_DESC}</p>
                            </div>
                            <Button
                                label={translatedText.CANCEL_SUBSCRIPTION || CANCEL_SUBSCRIPTION}
                                className="cancel-subscription-btn"
                                outlined
                                onClick={handleCancelSubscription}
                            />
                        </div>
                    ) : null;
                })()}

                {/* Right column: Current Plan Features + Upcoming Plan */}
                <div className="right-column">
                    {/* Current Plan Features */}
                    <div className="features-section">
                        <div className="section-header">
                            <span className="current-plan-label">{translatedText.CURRENT_PLAN_LABEL || CURRENT_PLAN_LABEL}</span>
                            <span className="current-plan-name">
                                {subscriptionInfoResponse?.data?.data?.hasSubscription
                                    ? subscriptionData.planName
                                    : translatedText.NO_ACTIVE_SUBSCRIPTION || NO_ACTIVE_SUBSCRIPTION}
                            </span>
                        </div>

                        <ul className="features-list">
                            {subscriptionData.features.map((feature: string, index: number) => (
                                <li key={index} className="feature-item">
                                    <i className="ri-check-line feature-icon" />
                                    <span>{feature}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Upcoming Plan Section - shown when next period uses a different plan */}
                    {hasUpcomingPlanChange && (
                        <div className="upcoming-plan-section">
                            <div className="upcoming-plan-header">
                                <i className="ri-calendar-schedule-line upcoming-plan-icon" />
                                <span className="upcoming-plan-label">{translatedText.UPCOMING_PLAN || UPCOMING_PLAN}</span>
                            </div>
                            <div className="upcoming-plan-name">{upcomingPlanName}</div>
                            <div className="upcoming-plan-description">{upcomingPlanDescription}</div>
                            <div className="upcoming-plan-start">
                                <i className="ri-calendar-line" />
                                <span>{translatedText.UPCOMING_PLAN_STARTS_ON || UPCOMING_PLAN_STARTS_ON}</span>
                                <span className="upcoming-start-date">{upcomingPlanStartDate}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Account Owner */}
                {/*<div className="account-owner-section">
                    <div className="section-title">
                        <i className="ri-user-line" />
                        <span>{translatedText.ACCOUNT_OWNER || ACCOUNT_OWNER}</span>
                    </div>
                    <div className="owner-info">
                        <div className="owner-avatar">
                            {subscriptionData.accountOwner.avatar ? (
                                <img src={subscriptionData.accountOwner.avatar} alt="Owner" />
                            ) : (
                                <div className="avatar-placeholder">{subscriptionData.accountOwner.name.charAt(0)}</div>
                            planStartDate: trialStartAt ? formatDate(trialStartAt) : 'N/A',
                        </div>
                        <div className="owner-details">
                            <div className="owner-name">{subscriptionData.accountOwner.name}</div>
                            <div className="owner-role">{subscriptionData.accountOwner.role}</div>
                        </div>
                        <span className="owner-status">{translatedText.ACTIVE || ACTIVE}</span>
                    </div>
                </div>
                */}
            </div>

            {/* Cancel Subscription Confirmation Dialog */}
            <CancelSubscriptionModal
                visible={showCancelDialog}
                onHide={() => setShowCancelDialog(false)}
                onConfirm={() => {
                    if (pendingCancelPayload) cancelSubscriptionMutate(pendingCancelPayload);
                }}
            />

            <MlxToast
                message={toastMessage.message}
                onClose={() => setToastMessage(defaultToastData)}
                show={toastMessage.show}
                variant={toastMessage.variant}
            />
        </MlxBlockUI>
    );
};

export default Overview;
